export type ActivityCoverageCategory =
  | "auth"
  | "deploy"
  | "app"
  | "config"
  | "domain"
  | "token"
  | "provider"
  | "admin"
  | "security";

export type ActivityCoverageManifestEntry = {
  id: string;
  category: ActivityCoverageCategory;
  source: string;
  actions: readonly string[];
  outcomes: readonly string[];
  requiredTests: readonly string[];
  method?: string;
  path?: string;
  module?: string;
};

export const ACTIVITY_COVERAGE_MANIFEST = [
  {
    id: "auth-token-email-sign-in",
    method: "POST",
    path: "/api/auth/sign-in",
    category: "auth",
    actions: ["login_success", "login_failure"],
    outcomes: ["success", "failure"],
    source: "server.auth",
    requiredTests: ["custom sign-in success/failure"],
  },
  {
    id: "auth-delegated-email-sign-in",
    method: "POST",
    path: "/api/auth/sign-in/email",
    category: "auth",
    actions: ["login_success", "login_failure"],
    outcomes: ["success", "failure"],
    source: "server.auth",
    requiredTests: ["escaped bug: direct sign-in/email invalid login is audited"],
  },
  {
    id: "auth-token-email-sign-up",
    method: "POST",
    path: "/api/auth/sign-up",
    category: "auth",
    actions: ["signup", "signup_failure", "signup_denied"],
    outcomes: ["success", "failure", "denied"],
    source: "server.auth",
    requiredTests: ["custom sign-up success/failure/denied"],
  },
  {
    id: "auth-delegated-email-sign-up",
    method: "POST",
    path: "/api/auth/sign-up/email",
    category: "auth",
    actions: ["signup", "signup_failure", "signup_denied"],
    outcomes: ["success", "failure", "denied"],
    source: "server.auth",
    requiredTests: ["direct sign-up/email success/failure is audited"],
  },
  {
    id: "auth-sign-out",
    method: "POST",
    path: "/api/auth/sign-out",
    category: "auth",
    actions: ["logout"],
    outcomes: ["success", "failure"],
    source: "server.auth",
    requiredTests: ["sign-out records the pre-logout session identity"],
  },
  {
    id: "auth-social-sign-in-start",
    method: "POST",
    path: "/api/auth/sign-in/social",
    category: "auth",
    actions: ["oauth_login_start", "oauth_login_failure"],
    outcomes: ["success", "failure"],
    source: "server.auth",
    requiredTests: ["social sign-in delegate start is audited"],
  },
  {
    id: "auth-oauth-handoff",
    method: "GET",
    path: "/api/auth/oauth/handoff",
    category: "auth",
    actions: ["oauth_login_success", "oauth_login_failure"],
    outcomes: ["success", "failure"],
    source: "server.auth",
    requiredTests: ["OAuth handoff success/failure"],
  },
  {
    id: "auth-oauth-exchange",
    method: "POST",
    path: "/api/auth/oauth/exchange",
    category: "auth",
    actions: ["oauth_token_exchange", "oauth_token_exchange_failure"],
    outcomes: ["success", "failure", "denied"],
    source: "server.auth",
    requiredTests: ["OAuth exchange success/failure/denied"],
  },
  {
    id: "auth-token-refresh",
    method: "POST",
    path: "/api/auth/token",
    category: "auth",
    actions: ["token_refresh", "token_refresh_failure"],
    outcomes: ["success", "failure", "denied"],
    source: "server.auth",
    requiredTests: ["token refresh success/failure"],
  },
  {
    id: "deploy-lifecycle",
    module: "packages/platform/gencow/apps-deploy-routes.ts",
    category: "deploy",
    actions: ["deploy_start", "deploy_success", "deploy_failure", "rollback"],
    outcomes: ["queued", "success", "failure"],
    source: "platform.apps.deploy",
    requiredTests: ["deploy activity capture tests"],
  },
  {
    id: "app-lifecycle",
    module: "packages/platform/gencow/apps-lifecycle.ts",
    category: "app",
    actions: ["app_create", "app_delete", "app_resume", "app_sleep", "app_recover"],
    outcomes: ["success", "failure"],
    source: "platform.apps.lifecycle",
    requiredTests: ["app lifecycle activity capture tests"],
  },
  {
    id: "env-config",
    module: "packages/platform/gencow/env-vars.ts",
    category: "config",
    actions: ["env_set", "env_unset"],
    outcomes: ["success", "failure", "denied"],
    source: "platform.env",
    requiredTests: ["env var activity capture and redaction tests"],
  },
  {
    id: "custom-domain",
    module: "packages/platform/gencow/apps-custom-domain.ts",
    category: "domain",
    actions: ["domain_set", "domain_remove", "domain_verify", "domain_rebind"],
    outcomes: ["success", "failure", "denied"],
    source: "platform.domain",
    requiredTests: ["custom-domain dual-write characterization"],
  },
  {
    id: "deploy-token",
    module: "packages/platform/gencow/apps-deploy-tokens.ts",
    category: "token",
    actions: ["deploy_token_create", "deploy_token_revoke"],
    outcomes: ["success", "failure", "denied"],
    source: "platform.deploy-tokens",
    requiredTests: ["deploy token activity capture tests"],
  },
  {
    id: "provider-admin",
    module: "packages/platform/gencow/admin-ai-provider-helpers.ts",
    category: "provider",
    actions: ["provider_key_create", "deployment_update", "rate_limit_policy_update"],
    outcomes: ["success", "failure", "denied"],
    source: "platform.provider-admin",
    requiredTests: ["provider admin dual-write characterization"],
  },
  {
    id: "platform-admin",
    module: "packages/platform/gencow/admin.ts",
    category: "admin",
    actions: ["admin_action"],
    outcomes: ["success", "failure", "denied"],
    source: "platform.admin",
    requiredTests: ["admin activity query and boundary tests"],
  },
  {
    id: "security-boundaries",
    module: "packages/platform/gencow/apps-activity.ts",
    category: "security",
    actions: ["owner_mismatch_denied", "invalid_token", "forbidden_admin_action"],
    outcomes: ["denied", "failure"],
    source: "platform.security",
    requiredTests: ["owner mismatch and non-admin broad query deny"],
  },
] as const satisfies readonly ActivityCoverageManifestEntry[];

export type BetterAuthActivityIntent = {
  successAction: string;
  failureAction: string;
  deniedAction?: string;
  source: string;
  readUserFromResponse?: boolean;
  sessionBeforeDelegate?: boolean;
};

function normalizeAuthPath(pathname: string): string {
  if (pathname === "/api/auth") return pathname;
  return pathname.replace(/\/+$/, "");
}

export function classifyBetterAuthRoute(method: string, pathname: string): BetterAuthActivityIntent | null {
  const normalizedMethod = method.toUpperCase();
  const normalizedPath = normalizeAuthPath(pathname);

  if (normalizedMethod === "POST" && normalizedPath === "/api/auth/sign-in/email") {
    return {
      successAction: "login_success",
      failureAction: "login_failure",
      source: "better-auth.sign-in-email",
      readUserFromResponse: true,
    };
  }

  if (normalizedMethod === "POST" && normalizedPath === "/api/auth/sign-up/email") {
    return {
      successAction: "signup",
      failureAction: "signup_failure",
      deniedAction: "signup_denied",
      source: "better-auth.sign-up-email",
      readUserFromResponse: true,
    };
  }

  if (normalizedMethod === "POST" && normalizedPath === "/api/auth/sign-out") {
    return {
      successAction: "logout",
      failureAction: "logout",
      source: "better-auth.sign-out",
      sessionBeforeDelegate: true,
    };
  }

  if (normalizedMethod === "POST" && normalizedPath === "/api/auth/sign-in/social") {
    return {
      successAction: "oauth_login_start",
      failureAction: "oauth_login_failure",
      source: "better-auth.sign-in-social",
    };
  }

  return null;
}

export function resolveBetterAuthActivityResult(
  intent: BetterAuthActivityIntent,
  status: number,
): { action: string; outcome: "success" | "failure" | "denied"; reasonCode: string | null } {
  if (status >= 200 && status < 400) {
    return { action: intent.successAction, outcome: "success", reasonCode: null };
  }
  if (status === 401 || status === 403) {
    return {
      action: intent.deniedAction ?? intent.failureAction,
      outcome: intent.deniedAction && status === 403 ? "denied" : "failure",
      reasonCode: status === 403 ? "auth_denied" : "auth_failed",
    };
  }
  return { action: intent.failureAction, outcome: "failure", reasonCode: "auth_failed" };
}
